import { IonButton } from '@ionic/react';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import styles from '@components/buttons/PrimaryButton.module.css';
import DarkButtonText from '@components/typography/DarkButtonText';
import DarkButtonIcon from '@components/typography/icons/DarkButtonIcon';
import useKeyboardFocus from '@hooks/useKeyboardFocus';
import { IconName } from '@utils/iconUtils';

type ButtonProps = Pick<
  ComponentProps<typeof IonButton>,
  'disabled' | 'expand' | 'href' | 'onClick' | 'onKeyUp' | 'rel' | 'routerLink' | 'tabIndex' | 'target' | 'type'
>;

export type Color =
  | 'teal'
  | 'midPurple'
  | 'pixiePowder'
  | 'pink'
  | 'primary50'
  | 'primary300'
  | 'success300'
  | 'neutral500';

type Props = ButtonProps & {
  color?: Color;
  disabled?: boolean;
  icon?: IconName;
  size?: 'small' | 'medium' | 'large';
  skipHover?: boolean;
  textKey?: string;
};

const PrimaryButton = ({ color = 'pixiePowder', icon, size = 'medium', skipHover, textKey, ...buttonProps }: Props) => {
  const keyboardFocus = useKeyboardFocus();

  return (
    <IonButton
      className={`${styles.button} ${keyboardFocus ? styles.focus : ''}  ${styles[color]} ${styles[size]} ${
        skipHover ? styles.skipHover : ''
      } ${!textKey ? styles.noText : ''}`}
      mode="md"
      {...buttonProps}
    >
      {textKey && (
        <DarkButtonText size={size}>
          <FormattedMessage id={textKey} />
        </DarkButtonText>
      )}
      {icon && (
        <span className={textKey ? styles.iconWithText : ''}>
          <DarkButtonIcon icon={icon} />
        </span>
      )}
    </IonButton>
  );
};

export default PrimaryButton;
